import { useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, Grid, MenuItem } from "@mui/material";
import {
  BoxBase,
  ButtonBase,
  FormControlBase,
  FormSectionTitle,
  InputLabelSelect,
  SelectBase,
  TextFieldBase,
} from "../../../styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PedidosContext } from "../../../../../Context/PedidosContext";
import api from "../../../../../Api/apiAxios";
import { format } from "date-fns";

const validationSchema = Yup.object({
  valorContrato: Yup.number().required("O valor do contrato é obrigatório"),
});

function toISODate(value) {
  if (!value) return "";
  try {
    return format(new Date(value), "yyyy-MM-dd");
  } catch {
    return "";
  }
}

function getNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function ModalContrato({ contratoHistoricoSelecionado, isShow = false }) {
  const { id } = useParams();
  const pedidoId = useMemo(() => Number(id || 0), [id]);

  const {
    setModalContrato,
    fetchContratos,
    motivosCancelamento,
    contrato,
  } = useContext(PedidosContext);

  const contratoIdSelecionado =
    contratoHistoricoSelecionado?.contratoId ??
    contratoHistoricoSelecionado?.ContratoId ??
    0;

  const valorInicial =
    contratoHistoricoSelecionado?.valor ??
    contratoHistoricoSelecionado?.Valor ??
    contrato?.valorAtual ??
    contrato?.valor ??
    "";

  const formik = useFormik({
    initialValues: {
      valorContrato: valorInicial,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isShow) return;

        const isNovoManual = !contratoIdSelecionado || contratoIdSelecionado <= 0;

        let contratosDoPedido = [];
        if (pedidoId > 0) {
          const lista = await fetchContratos(pedidoId);
          contratosDoPedido = Array.isArray(lista) ? lista : [];
        }

        const contratoAtivoNoPedido =
          contratosDoPedido.find((c) => getNumber(c.statusAtual ?? c.StatusAtual) === 1) ?? null;

        if (isNovoManual && contratoAtivoNoPedido) {
          alert("Não é permitido criar novo contrato manualmente: já existe contrato ativo para este pedido.");
          return;
        }

        const contratoExistenteMaisRecente =
          contratosDoPedido.length > 0
            ? [...contratosDoPedido].sort(
                (a, b) =>
                  new Date(b.dataUltimaAtualizacao ?? b.DataUltimaAtualizacao ?? 0) -
                  new Date(a.dataUltimaAtualizacao ?? a.DataUltimaAtualizacao ?? 0)
              )[0]
            : null;

        const contratoExistenteId = getNumber(contratoExistenteMaisRecente?.id ?? contratoExistenteMaisRecente?.Id, 0);
        const contratoExistenteStatus = getNumber(
          contratoExistenteMaisRecente?.statusAtual ?? contratoExistenteMaisRecente?.StatusAtual,
          -1
        );

        const contratoIdParaOperacao = contratoIdSelecionado > 0 ? contratoIdSelecionado : contratoExistenteId;

        const historicoPayload = {
          ContratoId: contratoIdParaOperacao > 0 ? contratoIdParaOperacao : null,
          Sistema:
            contrato?.sistema ??
            contratoExistenteMaisRecente?.sistema ??
            contratoExistenteMaisRecente?.Sistema ??
            contratoHistoricoSelecionado?.sistema ??
            contratoHistoricoSelecionado?.Sistema,
          PedidoId:
            contrato?.pedidoId ??
            contratoExistenteMaisRecente?.pedidoId ??
            contratoExistenteMaisRecente?.PedidoId ??
            contratoHistoricoSelecionado?.pedidoId ??
            contratoHistoricoSelecionado?.PedidoId ??
            pedidoId,
          DistribuidorId:
            contrato?.distribuidorId ??
            contratoExistenteMaisRecente?.distribuidorId ??
            contratoExistenteMaisRecente?.DistribuidorId ??
            contratoHistoricoSelecionado?.distribuidorId ??
            contratoHistoricoSelecionado?.DistribuidorId,
          ClienteId:
            contrato?.clienteId ??
            contratoExistenteMaisRecente?.clienteId ??
            contratoExistenteMaisRecente?.ClienteId ??
            contratoHistoricoSelecionado?.clienteId ??
            contratoHistoricoSelecionado?.ClienteId,
          NumeroSerie:
            contrato?.numeroSerie ??
            contratoExistenteMaisRecente?.numeroSerie ??
            contratoExistenteMaisRecente?.NumeroSerie ??
            contratoHistoricoSelecionado?.numeroSerie ??
            contratoHistoricoSelecionado?.NumeroSerie,
          TipoContrato:
            contrato?.tipoContrato ??
            contratoExistenteMaisRecente?.tipoContrato ??
            contratoExistenteMaisRecente?.TipoContrato ??
            contratoHistoricoSelecionado?.tipoContrato ??
            contratoHistoricoSelecionado?.TipoContrato,
          StatusContrato: 1,
          DataAtivacao: new Date(),
          DataAtualizacao: new Date(),
          ValorFixo: 1,
          Valor: Number(values.valorContrato),
          DataCancelamento: null,
          MotivoCancelamentoOpcao: null,
          MotivoCancelamento: null,
          CancelamentoReal: true,
          UsuarioIdCancelamento: null,
          NotificacaoDiferencaValorEnviada: 0,
        };

        if (contratoIdSelecionado > 0) {
          const valorOriginal = Number(contratoHistoricoSelecionado?.valor ?? contratoHistoricoSelecionado?.Valor ?? 0);
          if (Number(values.valorContrato) === valorOriginal) {
            console.log("O valor do contrato não foi alterado, não será feito update.");
            setModalContrato({ show: false, contrato: {} });
            return;
          }

          const idUsuario = window.localStorage.getItem('idUsuario');
          await api.post(`/Contrato/${contratoIdSelecionado}/alterar`, historicoPayload, {
            headers: { "Content-Type": "application/json" },
            params: { usuarioId: idUsuario }
          });

          setModalContrato({ show: false, contrato: {} });
          return;
        }

        if (contratoExistenteId > 0) {
          const idUsuario = window.localStorage.getItem('idUsuario');
          if (contratoExistenteStatus === 0) {
            await api.post(`/Contrato/${contratoExistenteId}/alterar`, historicoPayload, {
              headers: { "Content-Type": "application/json" },
              params: { usuarioId: idUsuario }
            });
          } else {
            await api.post(`/Contrato/${contratoExistenteId}/alterar`, historicoPayload, {
              headers: { "Content-Type": "application/json" },
              params: { usuarioId: idUsuario }
            });
          }
        } else {
          await api.post("/Contrato", historicoPayload, {
            headers: { "Content-Type": "application/json" },
          });
        }

        setModalContrato({ show: false, contrato: {} });
      } catch (e) {
        console.error(e);
        alert("Erro ao salvar contrato.");
      }
    },
  });

  useEffect(() => {
    if (valorInicial !== undefined && valorInicial !== null) {
      formik.setFieldValue("valorContrato", valorInicial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusContrato = Number(
    contratoHistoricoSelecionado?.statusContrato ?? contratoHistoricoSelecionado?.StatusContrato ?? 0
  );
  const isCancelado = isShow && statusContrato === 0;

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          py: 3,
          px: 1,
          my: 2,
          borderRadius: "30px",
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          width: "50%",
          alignSelf: "center",
          position: "fixed",
          top: 100,
          zIndex: 1000,
          maxHeight: "80vh",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
        }}
      >
        <FormSectionTitle
          label={
            contratoIdSelecionado > 0 && !isShow
              ? "Editar Contrato"
              : isShow
                ? "Visualizar Contrato"
                : "Novo Contrato"
          }
        />

        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            mt: 3,
            justifyContent: "center",
            width: "98%",
          }}
        >
          <BoxBase>
            <TextFieldBase
              label="Valor do Contrato (R$)"
              name="valorContrato"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.valorContrato && Boolean(formik.errors.valorContrato)}
              helperText={formik.touched.valorContrato && formik.errors.valorContrato}
              placeholder="0.00"
              type="number"
              width="100%"
              value={formik.values.valorContrato}
              disabled={isShow}
            />
          </BoxBase>
        </Container>

        {isCancelado && (
          <Grid container spacing={2} sx={{ pb: 2, px: 4, display: "flex" }}>
            <Grid item xs={4}>
              <FormControlBase>
                <TextFieldBase
                  label="Data de Cancelamento"
                  value={toISODate(
                    contratoHistoricoSelecionado?.dataCancelamento ??
                      contratoHistoricoSelecionado?.DataCancelamento
                  )}
                  type="date"
                  width="100%"
                  disabled
                />
              </FormControlBase>
            </Grid>

            <Grid item xs={8}>
              <FormControlBase>
                <InputLabelSelect id="motivoCancelamentoOpcao">Motivo</InputLabelSelect>
                <SelectBase
                  id="motivoCancelamentoOpcao"
                  value={
                    contratoHistoricoSelecionado?.motivoCancelamentoOpcao ??
                    contratoHistoricoSelecionado?.MotivoCancelamentoOpcao ??
                    0
                  }
                  label="Motivo"
                  width="100%"
                  disabled
                >
                  {motivosCancelamento.map((motivo) => (
                    <MenuItem key={motivo.id} value={motivo.id}>
                      {motivo.descricaoMotivo}
                    </MenuItem>
                  ))}
                </SelectBase>
              </FormControlBase>
            </Grid>

            <Grid item xs={12}>
              <FormControlBase>
                <TextFieldBase
                  label="Detalhes"
                  value={
                    contratoHistoricoSelecionado?.motivoCancelamento ??
                    contratoHistoricoSelecionado?.MotivoCancelamento ??
                    ""
                  }
                  width="100%"
                  multiline
                  rows={4}
                  disabled
                />
              </FormControlBase>
            </Grid>
          </Grid>
        )}

        {isShow ? (
          <Box sx={{ display: "flex", width: "95%", justifyContent: "flex-end", gap: 2, my: 1 }}>
            <ButtonBase
              variant="outlined"
              onClick={() => setModalContrato({ show: false, contrato: {} })}
            >
              Voltar
            </ButtonBase>
          </Box>
        ) : (
          <Box sx={{ display: "flex", width: "95%", justifyContent: "flex-end", gap: 2, my: 1 }}>
            <ButtonBase
              variant="outlined"
              onClick={() => setModalContrato({ show: false, contrato: {} })}
            >
              Cancelar
            </ButtonBase>
            <ButtonBase onClick={formik.submitForm} variant="contained" disabled={formik.isSubmitting}>
              Salvar
            </ButtonBase>
          </Box>
        )}
      </Box>
    </Box>
  );
}
